import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { SearchSelect, formatDate } from './FieldComponents';
import { CloudSync, Plus, RefreshCwOff, X } from 'lucide-react';
import { getContactInitialsClassName } from '../utils/colorUtils';
import { useNavVariant } from '../App';
import Modal from './Modal';
import { fetchNavSearchResults, toCaseSelectionOptions } from '../utils/navSearch';
import { EntityHoverPreview } from './EntityHoverPreview';

const getRelatedCaseContactMediaUrl = (value) => {
    if (!value) return '';

    const stringValue = String(value).trim();
    if (stringValue.includes('contact-media.php')) {
        return stringValue.startsWith('http')
            ? stringValue
            : `https://api.casedb.co/${stringValue.replace(/^\/+/, '')}`;
    }

    if (stringValue.includes('uploads/contacts/')) {
        let uploadPath = stringValue;
        try {
            if (stringValue.startsWith('http')) {
                uploadPath = new URL(stringValue).pathname;
            }
        } catch {
            uploadPath = stringValue;
        }
        try {
            uploadPath = decodeURIComponent(uploadPath);
        } catch {
            // Keep the stored path when it contains a malformed escape sequence.
        }
        uploadPath = uploadPath.replace(/^\/+/, '');
        return `https://api.casedb.co/contact-media.php?file=${encodeURIComponent(uploadPath)}`;
    }

    return stringValue.startsWith('http')
        ? stringValue
        : `https://api.casedb.co/${stringValue.replace(/^\/+/, '')}`;
};

export const RelatedCases = ({ case_id, lead_id }) => {
    const [contactRelatedCases, setContactRelatedCases] = useState([]);
    const [manualRelatedCases, setManualRelatedCases] = useState([]);
    const [fuzzyNameCases, setFuzzyNameCases] = useState([]);
    const [currentParentCaseId, setCurrentParentCaseId] = useState(null);
    const [currentChildCaseIds, setCurrentChildCaseIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [relationshipModalCase, setRelationshipModalCase] = useState(null);
    const [relationshipSaving, setRelationshipSaving] = useState(false);
    const [relationshipError, setRelationshipError] = useState('');
    const navigate = useNavigate();
    const navVariant = useNavVariant();

    const fetchRelatedCases = async () => {
        const response = await fetch(`https://api.casedb.co/related_cases.php?case_id=${case_id}&lead_id=${lead_id}`, {
            credentials: 'include'
        });
        const data = await response.json();
        if (data.success) {
            setContactRelatedCases(data.contact_related_cases || []);
            setManualRelatedCases(data.manual_related_cases || []);
            setFuzzyNameCases(data.fuzzy_name_cases || []);
            setCurrentParentCaseId(data.parent_case_id ? Number(data.parent_case_id) : null);
            setCurrentChildCaseIds((data.child_case_ids || []).map(id => Number(id)).filter(Boolean));
        } else {
            console.error('Failed to fetch related cases:', data.message);
        }
    }

    const loadAvailableCases = useCallback(async (query = '') => {
        const results = await fetchNavSearchResults(query, { type: 'cases', limit: 15 });
        const linkedCaseIds = new Set(manualRelatedCases.map(c => parseInt(c.id, 10)));
        const currentCaseId = parseInt(case_id, 10);

        return toCaseSelectionOptions(results).filter((item) => {
            const itemId = parseInt(item.value, 10);
            return itemId !== currentCaseId && !linkedCaseIds.has(itemId);
        });
    }, [case_id, manualRelatedCases]);

    const createRelatedCaseConnection = async (selectedCase, parentCaseId = null) => {
        const payload = {
            case_id: case_id,
            related_case_id: selectedCase
        };

        if (parentCaseId) {
            payload.parent_case_id = parentCaseId;
        }

        const response = await fetch('https://api.casedb.co/related_cases.php', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload)
        });

        return response.json();
    };

    const addRelatedCase = async (selectedCase) => {
        if (!selectedCase) return;
        
        setLoading(true);
        try {
            const data = await createRelatedCaseConnection(selectedCase, case_id);
            if (data.success) {
                await fetchRelatedCases();
            } else {
                console.error('Failed to add related case:', data.message);
            }
        } catch (error) {
            console.error('Error adding related case:', error);
        }
        setLoading(false);
    }

    const removeRelatedCase = async (caseItem) => {
        const relatedCaseId = caseItem?.id || caseItem;
        if (!relatedCaseId) return;

        const confirmationMessage = caseItem?.is_hierarchy_linked_case
            ? `Unlink ${caseItem.name || 'this case'} and remove the ${caseItem.linked_case_type || 'parent/child'} relationship?`
            : 'Are you sure you want to remove this linked case?';
        if (!window.confirm(confirmationMessage)) return;
        
        setLoading(true);
        try {
            const response = await fetch('https://api.casedb.co/related_cases.php', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    case_id: case_id,
                    related_case_id: relatedCaseId
                })
            });
            
            const data = await response.json();
            if (data.success) {
                await fetchRelatedCases();
            } else {
                console.error('Failed to remove related case:', data.message);
            }
        } catch (error) {
            console.error('Error removing related case:', error);
        }
        setLoading(false);
    }

    const openRelationshipModal = (caseItem, event) => {
        event?.stopPropagation?.();
        setRelationshipError('');
        setRelationshipModalCase(caseItem);
    };

    const closeRelationshipModal = () => {
        if (relationshipSaving) return;
        setRelationshipError('');
        setRelationshipModalCase(null);
    };

    const relationshipModalCaseId = relationshipModalCase?.id ? Number(relationshipModalCase.id) : null;
    const relationshipModalCaseChildIds = (relationshipModalCase?.child_case_ids || []).map(id => Number(id));
    const relationshipModalOtherChildIds = relationshipModalCaseChildIds.filter(id => id && id !== Number(case_id));
    const relationshipHasOtherChildren = relationshipModalOtherChildIds.length > 0;
    const isCurrentParentSelected = relationshipModalCaseId && currentParentCaseId && relationshipModalCaseId === Number(currentParentCaseId);
    const isCurrentChildSelected = relationshipModalCase?.linked_case_type === 'child' || Number(relationshipModalCase?.parent_case_id) === Number(case_id);
    const isModalCaseConnected = relationshipModalCaseId ? manualRelatedCases.some(caseItem => Number(caseItem.id) === relationshipModalCaseId) : false;
    const currentCaseHasChildren = currentChildCaseIds.length > 0;
    const makeParentDisabled = relationshipSaving || relationshipModalCase?.linked_case_type === 'parent';
    const makeChildDisabled = relationshipSaving || relationshipModalCase?.linked_case_type === 'child' || (isCurrentParentSelected && relationshipHasOtherChildren);
    const showMakeParentButton = !currentCaseHasChildren && !isCurrentChildSelected && !makeParentDisabled;

    const getMakeChildDisabledReason = () => {
        if (!relationshipModalCase) return '';
        if (relationshipModalCase.linked_case_type === 'child') {
            return 'This linked case is already a child of the current case.';
        }
        if (isCurrentParentSelected && relationshipHasOtherChildren) {
            return 'The current parent cannot become a child while it still has other child cases.';
        }
        return '';
    };

    const updateRelationship = async (mode) => {
        if (!relationshipModalCase?.id || !case_id) return;

        setRelationshipSaving(true);
        setRelationshipError('');

        try {
            if (mode === 'connect') {
                const connectData = await createRelatedCaseConnection(relationshipModalCase.id);
                if (!connectData.success) {
                    throw new Error(connectData.message || 'Failed to connect linked case');
                }
            } else if (mode === 'disconnect') {
                const clearParentPayload = relationshipModalCase.linked_case_type === 'parent'
                    ? { action: 'clear_parent', case_id: Number(case_id) }
                    : relationshipModalCase.linked_case_type === 'child'
                        ? { action: 'clear_parent', case_id: Number(relationshipModalCase.id) }
                        : null;

                if (clearParentPayload) {
                    const clearParentResponse = await fetch('https://api.casedb.co/related_cases.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(clearParentPayload)
                    });
                    const clearParentData = await clearParentResponse.json();
                    if (!clearParentData.success) {
                        throw new Error(clearParentData.message || 'Failed to clear parent/child relationship');
                    }
                }

                const unlinkResponse = await fetch('https://api.casedb.co/related_cases.php', {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        case_id: case_id,
                        related_case_id: relationshipModalCase.id
                    })
                });
                const unlinkData = await unlinkResponse.json();
                if (!unlinkData.success) {
                    throw new Error(unlinkData.message || 'Failed to disconnect linked cases');
                }
            } else if (mode === 'parent') {
                if (!isModalCaseConnected) {
                    const connectData = await createRelatedCaseConnection(relationshipModalCase.id);
                    if (!connectData.success) {
                        throw new Error(connectData.message || 'Failed to connect linked case');
                    }
                }

                if (isCurrentChildSelected) {
                    const clearExistingChildResponse = await fetch('https://api.casedb.co/related_cases.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            action: 'clear_parent',
                            case_id: Number(relationshipModalCase.id)
                        })
                    });
                    const clearExistingChildData = await clearExistingChildResponse.json();
                    if (!clearExistingChildData.success) {
                        throw new Error(clearExistingChildData.message || 'Failed to clear existing child relationship');
                    }
                }

                const response = await fetch('https://api.casedb.co/related_cases.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'set_parent',
                        case_id: Number(case_id),
                        parent_case_id: Number(relationshipModalCase.id)
                    })
                });
                const data = await response.json();
                if (!data.success) {
                    throw new Error(data.message || 'Failed to set parent case');
                }
            } else if (mode === 'child') {
                if (!isModalCaseConnected) {
                    const connectData = await createRelatedCaseConnection(relationshipModalCase.id);
                    if (!connectData.success) {
                        throw new Error(connectData.message || 'Failed to connect linked case');
                    }
                }

                if (isCurrentParentSelected) {
                    if (relationshipHasOtherChildren) {
                        throw new Error('The current parent cannot become a child while it still has other child cases.');
                    }

                    const clearCurrentParentResponse = await fetch('https://api.casedb.co/related_cases.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            action: 'clear_parent',
                            case_id: Number(case_id)
                        })
                    });
                    const clearCurrentParentData = await clearCurrentParentResponse.json();
                    if (!clearCurrentParentData.success) {
                        throw new Error(clearCurrentParentData.message || 'Failed to clear current parent relationship');
                    }
                }

                const response = await fetch('https://api.casedb.co/related_cases.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'set_parent',
                        case_id: Number(relationshipModalCase.id),
                        parent_case_id: Number(case_id)
                    })
                });
                const data = await response.json();
                if (!data.success) {
                    throw new Error(data.message || 'Failed to set child case');
                }
            }

            await fetchRelatedCases();
            closeRelationshipModal();
        } catch (error) {
            setRelationshipError(error.message || 'Failed to update linked case relationship');
        } finally {
            setRelationshipSaving(false);
        }
    };

    const getContactDisplay = (caseItem) => {
        const profilePicture = caseItem?.profile_picture;
        const hasImage = profilePicture?.includes('contact-media.php')
            || profilePicture?.includes('uploads')
            || profilePicture?.startsWith('http');
        if (hasImage) {
            const imageUrl = getRelatedCaseContactMediaUrl(profilePicture);
            return <img src={imageUrl} alt="Profile" className="contact-initials" />;
        }
        
        const initials = profilePicture
            || (caseItem?.name?.includes(" ")
                ? caseItem.name.charAt(0) + caseItem.name.split(" ")[1].charAt(0)
                : caseItem?.name?.charAt(0) || "DB");
        const className = getContactInitialsClassName('mini-case-initials', caseItem.id);
        
        return <span className={className}>{initials}</span>;
    };

    useEffect(() => {
        fetchRelatedCases();
    }, [case_id, lead_id]);

    const isCaseConnected = (caseItem) => {
        if (!caseItem?.id) return false;
        return manualRelatedCases.some(relatedCase => Number(relatedCase.id) === Number(caseItem.id));
    };

    const renderCaseItem = (caseItem, index, isManual = false, isNamed = false) => {
        const caseIsConnected = isCaseConnected(caseItem) || isManual;

        return (
            <EntityHoverPreview
                as="div"
                entityType="case"
                entityId={caseItem.id}
                className="mini-case-summary"
                key={`${caseItem.id}-${index}`}
                onClick={() => navigate(`/case/${caseItem.id}`)}
            >
                {getContactDisplay(caseItem)}
                <div className="mini-case-meta" onClick={() => navigate(`/case/${caseItem.id}`)}>
                    <div className="mini-case-name">
                        <span className='mini-case-name-text'>
                            {caseItem.name}
                        </span>
                        <span className='mini-case-id subtext'>
                            ({caseItem.id})
                            {caseItem.is_child_case && (
                                <span className='subtext'>
                                    &nbsp;(Child Case)
                                </span>
                            )}
                            {caseItem.is_parent_case && (
                                <span className='subtext'>
                                    &nbsp;(Parent Case)
                                </span>
                            )}
                        </span>
                    </div>
                    <div className='mini-case-info subtext'>
                        {caseItem.field_name && !isManual && (
                            <span>
                                Found in: <span  className="field-connection">{caseItem.field_name}</span>
                            </span>
                        )}
                        <div className='mini-case-vitals' style={{ display: "flex", flexWrap: 'wrap', alignItems: "center", justifyContent: "flex-start", gap: "2.5px" }}>
                            {caseItem.vitals?.first_primary && (
                                <>
                                    <span>{caseItem?.vitals?.first_primary?.[0]}{caseItem?.vitals?.first_primary?.[1] ? ` (@${caseItem?.vitals?.first_primary?.[1]})` : null}</span>
                                    {caseItem.vitals?.phase || caseItem.vitals?.incident_date ? "•" : null}
                                </>
                            )}
                            {caseItem.vitals?.phase && (
                                <>
                                    <span>{caseItem.vitals.phase}</span>
                                    {caseItem.vitals?.incident_date ? "•" : null}
                                </>
                            )}
                            {caseItem.vitals?.incident_date && (
                                <>
                                    <span>{formatDate(caseItem.vitals.incident_date)[0]}</span>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                {isManual && (
                    <div className="case-actions">
                        <X
                            onClick={(e) => {
                                e.stopPropagation();
                                removeRelatedCase(caseItem);
                            }}
                            disabled={loading}
                            title={caseItem.is_hierarchy_linked_case ? 'Unlink parent/child case' : 'Remove linked case'}
                            size={18}
                        />
                    </div>
                )}
                {caseIsConnected ? (
                    <div className='link-type-container'>
                        {caseItem.linked_case_type ? (
                            <CloudSync
                                className="linked-case-type-icon"
                                size={16}
                                title={caseItem.linked_case_type}
                                onClick={(e) => openRelationshipModal(caseItem, e)}
                            />
                        ) : (
                            <RefreshCwOff
                                className="linked-case-type-icon"
                                size={14}
                                title="Manage connection"
                                onClick={(e) => openRelationshipModal(caseItem, e)}
                            />
                        )}
                    </div>
                ) : (
                    <div className='connect-case-container'>
                        <Plus
                            className="linked-case-type-icon"
                            size={18}
                            title="Connect case"
                            onClick={(e) => openRelationshipModal(caseItem, e)}
                        />
                    </div>
                )}
            </EntityHoverPreview>
        );
    };

    return (
        <div className="case-section" style={ navVariant === 'nav' ? { paddingBottom: '80px' } : navVariant === 'compact' ? { paddingBottom: '35px' } : { paddingBottom: '50px' } }>
            {contactRelatedCases.length > 0 ? (
                <div className="linked-cases-section related-cases">
                    <h3>Related Cases</h3>
                    <div className="case-list">
                        {contactRelatedCases.map((caseItem, index) => renderCaseItem(caseItem, index, false, false))}
                    </div>
                </div>
            ) : (
                <div className="linked-cases-section related-cases">
                    <h3>Related Cases</h3>
                    <div className="no-cases">
                        <p className='subtext'>No related cases found for this case. Reference this case's contact in other cases for them to appear here.</p>
                    </div>
                </div>
            )}

            {/* Manual Related Cases Section */}
            <div className="related-cases-section related-cases">
                <h3>Linked Cases</h3>

                <div className='form-group' style={{ marginBottom: "15px" }}>
                    <SearchSelect
                        onChange={(value) => {
                            addRelatedCase(value);
                        }}
                        loadOptions={loadAvailableCases}
                        loadOptionsMinChars={2}
                        placeholder="Search and select a case to link..."
                    />
                </div>

                {manualRelatedCases.length > 0 ? (
                    <div className="case-list">
                        {manualRelatedCases.map((caseItem, index) => renderCaseItem(caseItem, index, true, false))}
                    </div>
                ) : (
                    <div className="no-cases">
                        <p className="subtext">No manually linked cases.</p>
                    </div>
                )}
            </div>

            <div className="fuzzy-name-cases-section related-cases">
                <div className="case-list-header">
                    <h3>
                        Cases with Similar Names
                        <span className="case-count">  - {fuzzyNameCases.length} Cases</span>
                    </h3>
                </div>
                {fuzzyNameCases.length > 0 ? (
                    <div className="case-list">
                        {fuzzyNameCases.map((caseItem, index) => renderCaseItem(caseItem, index, false, true))}
                    </div>
                ) : (
                    <div className="no-cases">
                        <p className="subtext">No cases with similar names found.</p>
                    </div>
                )}
            </div>

            {relationshipModalCase && (
                <Modal
                    onClose={closeRelationshipModal}
                    title="Manage Case Connection"
                    wide
                    footer={(
                        <div className='modal-footer-actions'>
                            <button className='action alt' onClick={closeRelationshipModal} disabled={relationshipSaving}>Cancel</button>
                            {!isModalCaseConnected && (
                                <button className='action' onClick={() => updateRelationship('connect')} disabled={relationshipSaving}>
                                    Connect
                                </button>
                            )}
                            {isModalCaseConnected && (
                                <button className='action destroy' onClick={() => updateRelationship('disconnect')} disabled={relationshipSaving}>
                                    Disconnect
                                </button>
                            )}
                            {showMakeParentButton && <button className='action quaternary' onClick={() => updateRelationship('parent')} disabled={makeParentDisabled}>
                                Make Parent
                            </button>}
                            {!makeChildDisabled && <button className='action' onClick={() => updateRelationship('child')} disabled={makeChildDisabled}>
                                Make Child
                            </button>}
                        </div>
                    )}
                >
                    <div className='modal-content-wrapper'>
                        <div className='linked-case-modal-summary'>
                            <strong>Current Case:</strong> {case_id}
                        </div>
                        <div className='linked-case-modal-summary'>
                            <strong>Linked Case:</strong> {relationshipModalCase.name} ({relationshipModalCase.id})
                        </div>
                        <div className='linked-case-modal-summary'>
                            <strong>Connection Status:</strong> {isModalCaseConnected ? 'Connected' : 'Not connected'}
                        </div>
                        <div className='linked-case-modal-summary'>
                            <strong>Current Relationship:</strong> {relationshipModalCase.linked_case_type || 'No parent/child relationship set'}
                        </div>
                        <p className='small-text' style={{ color: 'var(--subtext-color)' }}>
                            Use <strong>Connect</strong> to create a linked-case relationship only. <strong>Make Parent</strong> and <strong>Make Child</strong> will connect the case automatically if it is not already connected.
                        </p>
                        {currentParentCaseId && relationshipModalCaseId && currentParentCaseId !== relationshipModalCaseId && (
                            <p className='small-text' style={{ color: 'var(--subtext-color)' }}>
                                The current case can only have one parent. Choosing <strong>Make Parent</strong> will replace the current parent relationship.
                            </p>
                        )}
                        {getMakeChildDisabledReason() && (
                            <p className='small-text' style={{ color: 'var(--priority-color)' }}>
                                {getMakeChildDisabledReason()}
                            </p>
                        )}
                        {relationshipError && <p className='subtext' style={{ color: 'var(--priority-color)' }}>{relationshipError}</p>}
                    </div>
                </Modal>
            )}
        </div>
    );
}
